import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Plus, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Field, Loading, PageHeader, SimpleSelect, btnGhost, btnPrimary, inputCls } from "@/components/app/common";
import { useAuth } from "@/context/AuthContext";

const genPwd = () => `Cosm${Math.random().toString(36).slice(2, 8)}!${Math.floor(Math.random() * 90 + 10)}`;

function UserDialog({ open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "collaboratore" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { if (open) setF({ name: "", email: "", password: genPwd(), role: "collaboratore" }); }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/users", f);
      toast.success("Utente creato", { description: `Comunica a ${f.email} la password temporanea: ${f.password}`, duration: 12000 });
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="user-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display font-medium text-2xl">Invita un collaboratore</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Nome e cognome"><Input data-testid="user-name-input" value={f.name} onChange={(e) => set("name")(e.target.value)} className={inputCls} /></Field>
          <Field label="Email"><Input data-testid="user-email-input" type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} className={inputCls} /></Field>
          <Field label="Password temporanea" hint="Comunicala al collaboratore: potrà usarla per accedere.">
            <div className="flex gap-2">
              <Input data-testid="user-password-input" value={f.password} onChange={(e) => set("password")(e.target.value)} className={`${inputCls} font-mono`} />
              <Button type="button" variant="outline" className={`${btnGhost} w-10 p-0 shrink-0`} onClick={() => set("password")(genPwd())} data-testid="user-generate-password" title="Genera"><Wand2 className="h-4 w-4" /></Button>
            </div>
          </Field>
          <Field label="Ruolo"><SimpleSelect testId="user-role-select" value={f.role} onChange={set("role")} options={[{ value: "collaboratore", label: "Collaboratore" }, { value: "admin", label: "Amministratore" }]} /></Field>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button className={btnPrimary} onClick={save} disabled={saving} data-testid="user-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea utente"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetDialog({ user, onClose }) {
  const [pwd, setPwd] = useState("");
  useEffect(() => { if (user) setPwd(genPwd()); }, [user]);
  const save = async () => {
    try { await api.post(`/users/${user.id}/password`, { password: pwd }); toast.success("Password reimpostata", { description: `Nuova password temporanea: ${pwd}`, duration: 12000 }); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="reset-password-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display font-medium text-2xl">Reimposta password</DialogTitle></DialogHeader>
        <Field label={`Nuova password per ${user?.email}`}><Input data-testid="reset-password-input" value={pwd} onChange={(e) => setPwd(e.target.value)} className={`${inputCls} font-mono`} /></Field>
        <DialogFooter className="gap-2">
          <Button variant="outline" className={btnGhost} onClick={onClose}>Annulla</Button>
          <Button className={btnPrimary} onClick={save} data-testid="reset-password-save">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Utenti() {
  const { user: me } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [reset, setReset] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (u, active) => {
    try { await api.patch(`/users/${u.id}`, { active }); toast.success(active ? "Utente riattivato" : "Utente disattivato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="utenti-page">
      <PageHeader overline="Team" title="Gestione utenti" subtitle="I collaboratori possono registrare movimenti, spese ricorrenti e vendite, ma non vedono utile, margini e costi d'acquisto.">
        <Button className={btnPrimary} onClick={() => setOpen(true)} data-testid="add-user-button"><Plus className="h-4 w-4 mr-1" />Invita collaboratore</Button>
      </PageHeader>
      {!items ? <Loading /> : (
        <div className="card-soft overflow-x-auto" data-testid="utenti-table">
          <table className="w-full text-sm min-w-[680px]">
            <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
              <tr>{["Nome", "Email", "Ruolo", "Creato il", "Attivo", ""].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#EAE3DC]">
              {items.map((u, i) => (
                <tr key={u.id} data-testid={`user-row-${i}`} className={u.active ? "" : "opacity-55"}>
                  <td className="px-4 py-3 font-medium">{u.name}{u.id === me.id && <span className="ml-2 text-[10px] text-[#756B64]">(tu)</span>}</td>
                  <td className="px-4 py-3 text-[#5E4A42]">{u.email}</td>
                  <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-[#E8C5C8]/60 text-[#77594E]" : "bg-[#8AA090]/20 text-[#4E6656]"}`}>{u.role === "admin" ? "Amministratore" : "Collaboratore"}</span></td>
                  <td className="px-4 py-3 tabular text-[#756B64]">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3"><Switch data-testid={`user-active-toggle-${i}`} checked={u.active} disabled={u.id === me.id} onCheckedChange={(v) => toggle(u, v)} /></td>
                  <td className="px-4 py-3 text-right"><button data-testid={`user-reset-password-${i}`} onClick={() => setReset(u)} className="text-xs inline-flex items-center gap-1 text-[#8C6D62] hover:underline"><KeyRound className="h-3.5 w-3.5" />Reimposta password</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <UserDialog open={open} onOpenChange={setOpen} onSaved={load} />
      <ResetDialog user={reset} onClose={() => setReset(null)} />
    </div>
  );
}
